
import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, effect, inject, DOCUMENT } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  EMPTY,
  Observable,
  Subscription,
  fromEvent,
  interval,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  retry,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import { AuthenticationApiService } from '../../api';
import { LOG_MODULE, LoggerService } from '../../core/logging';
import { ActivityTrackerService } from './activity-tracker.service';
import { AuthService } from './auth.service';
import { SessionExpiryService } from './session-expiry.service';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
const ACTIVITY_WINDOW_MS = 5 * 60 * 1000;
const HEARTBEAT_RETRY_DELAY_MS = 5_000;
const HEARTBEAT_RETRY_COUNT = 2;

@Injectable({ providedIn: 'root' })
export class SessionKeepaliveService {
  private readonly document: Document = inject(DOCUMENT);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly authApi: AuthenticationApiService = inject(
    AuthenticationApiService,
  );
  private readonly authService: AuthService = inject(AuthService);
  private readonly sessionExpiry: SessionExpiryService = inject(SessionExpiryService);
  private readonly activity: ActivityTrackerService = inject(ActivityTrackerService);

  private subscription: Subscription | null = null;

  constructor() {
    effect(() => {
      const authenticated: boolean = this.authService.isAuthenticatedSignal();

      if (authenticated) {
        this.start();
      } else {
        this.stop();
      }
    });
  }

  private start(): void {
    if (this.subscription) {
      return;
    }

    const visible$ = fromEvent(this.document, 'visibilitychange').pipe(
      map(() => !this.document.hidden),
      startWith(!this.document.hidden),
    );

    const heartbeatSub: Subscription = interval(HEARTBEAT_INTERVAL_MS)
      .pipe(
        withLatestFrom(visible$),
        filter(([, visible]) => visible),
        filter(() => Date.now() - this.activity.lastActivityAt() < ACTIVITY_WINDOW_MS),
        filter(() => this.authService.isAuthenticatedSignal()),
        switchMap(() =>
          this.authApi.authControllerHeartbeat().pipe(
            retry({
              count: HEARTBEAT_RETRY_COUNT,
              delay: (error: unknown): Observable<number> => {
                if (
                  error instanceof HttpErrorResponse &&
                  error.status === 401
                ) {
                  return throwError(() => error);
                }
                return timer(HEARTBEAT_RETRY_DELAY_MS);
              },
              resetOnSuccess: true,
            }),
            catchError((error: unknown) => {
              if (
                error instanceof HttpErrorResponse &&
                error.status === 401
              ) {
                this.logger.warn('Heartbeat rejected, session expired', error, {
                  module: LOG_MODULE.sessionKeepalive,
                  reason: 'unauthorized',
                });
                this.sessionExpiry.handle(error, 'heartbeat');
                return EMPTY;
              }
              this.logger.warn('Heartbeat failed after retries', error, {
                module: LOG_MODULE.sessionKeepalive,
                reason: 'retries_exhausted',
                retryCount: HEARTBEAT_RETRY_COUNT,
              });
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => undefined,
        error: () => this.stop(),
      });

    this.subscription = heartbeatSub;
  }

  private stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }
}
